import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { Colors } from "../../../configs";
import { ICustomerActivitiesByDayResponse } from "../../../typings/interfaces/responses/customer-activities-by-day-response";

interface PatternsTabProps {
  activitiesByDay: ICustomerActivitiesByDayResponse | null;
  topServiceName?: string;
  totalBillItemCount?: number;
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function StatPill({ label, value, helper, color }: { label: string; value: string; helper: string; color: string }) {
  return (
    <Box
      sx={{
        borderRadius: "1rem",
        border: `1px solid ${Colors.ACCENT_GRAY}`,
        background: Colors.WHITE,
        p: "1rem",
      }}
    >
      <Typography
        sx={{
          fontSize: "var(--fs-2xs)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: `${Colors.MAIN_GRAY}8C`,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ mt: "0.5rem", fontSize: "var(--fs-md)", fontWeight: "bold", color, lineHeight: 1 }}
      >
        {value}
      </Typography>
      <Typography sx={{ mt: "0.25rem", fontSize: "var(--fs-2xs)", fontWeight: 600, color: `${Colors.MAIN_GRAY}99` }}>
        {helper}
      </Typography>
    </Box>
  );
}

const PatternsTab: FC<PatternsTabProps> = ({ activitiesByDay, topServiceName, totalBillItemCount }) => {
  const dropData = DAYS.map((day) => activitiesByDay?.averageCheckinsPerDay[day] ?? 0);
  const pickData = DAYS.map((day) => activitiesByDay?.averagePickupsPerDay[day] ?? 0);
  const totals = DAYS.map((_, i) => dropData[i] + pickData[i]);

  const max = Math.max(Number.EPSILON, ...dropData, ...pickData);
  const busiestIdx = totals.indexOf(Math.max(...totals));
  const busiestDay = DAY_SHORT[busiestIdx] ?? "—";

  const sumDrop = dropData.reduce((s, v) => s + v, 0);
  const sumPick = pickData.reduce((s, v) => s + v, 0);

  const MAX_BAR_REM = 4.5;
  const MIN_BAR_REM = 0.375;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* Top 3 stat pills */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" }}>
        <StatPill
          label="Most Active Day"
          value={busiestDay}
          helper={`${totals[busiestIdx] ?? 0} total visits`}
          color={Colors.SECONDARY}
        />
        <StatPill
          label="Top Service"
          value={topServiceName || "—"}
          helper="Highest usage category"
          color={Colors.PRIMARY}
        />
        <StatPill
          label="Lifetime Orders"
          value={totalBillItemCount?.toString() ?? "—"}
          helper="Total items processed"
          color={Colors.PRIMARY}
        />
      </Box>

      {/* Bar chart */}
      <Box
        sx={{
          borderRadius: "1rem",
          border: `1px solid ${Colors.ACCENT_GRAY}`,
          background: Colors.WHITE,
          p: "1rem",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "0.75rem" }}>
          <Box>
            <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: "bold", color: Colors.MAIN_GRAY }}>
              Visit Patterns
            </Typography>
            <Typography sx={{ mt: "0.25rem", fontSize: "var(--fs-2xs)", color: `${Colors.MAIN_GRAY}99` }}>
              Average drop-offs vs pickups per day of week
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "1rem" }}>
            {[
              { color: Colors.PRIMARY, label: "Drop-offs" },
              { color: Colors.SECONDARY, label: "Pickups" },
            ].map(({ color, label }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                <Box sx={{ width: "0.75rem", height: "0.75rem", borderRadius: "0.2rem", background: color }} />
                <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: `${Colors.MAIN_GRAY}B3` }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "0.5rem",
            borderRadius: "1rem",
            background: Colors.BACKSHADE_GRAY,
            p: "0.75rem",
            alignItems: "end",
          }}
        >
          {DAY_SHORT.map((day, i) => {
            const isBusiest = i === busiestIdx;
            const dropH = Math.max(MIN_BAR_REM, (dropData[i] / max) * MAX_BAR_REM);
            const pickH = Math.max(MIN_BAR_REM, (pickData[i] / max) * MAX_BAR_REM);

            return (
              <Box
                key={day}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: "0.75rem",
                  p: "0.5rem 0.25rem 0.4rem",
                  background: isBusiest ? `${Colors.SECONDARY}1A` : Colors.WHITE,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "0.25rem",
                    height: `${MAX_BAR_REM + 0.5}rem`,
                  }}
                >
                  {/* Drop-off bar */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.125rem" }}>
                    <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: "bold", color: Colors.PRIMARY }}>
                      {dropData[i]}
                    </Typography>
                    <Box
                      sx={{
                        width: "0.625rem",
                        borderRadius: "0.25rem 0.25rem 0 0",
                        height: `${dropH}rem`,
                        background: `linear-gradient(to top, ${Colors.PRIMARY}, ${Colors.PRIMARY}CC)`,
                      }}
                    />
                  </Box>
                  {/* Pickup bar */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.125rem" }}>
                    <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: "bold", color: Colors.SECONDARY }}>
                      {pickData[i]}
                    </Typography>
                    <Box
                      sx={{
                        width: "0.625rem",
                        borderRadius: "0.25rem 0.25rem 0 0",
                        height: `${pickH}rem`,
                        background: `linear-gradient(to top, ${Colors.SECONDARY}, ${Colors.SECONDARY}CC)`,
                      }}
                    />
                  </Box>
                </Box>
                <Typography sx={{ mt: "0.25rem", fontSize: "var(--fs-2xs)", fontWeight: "bold", color: Colors.MAIN_GRAY }}>
                  {day}
                </Typography>
                <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: `${Colors.MAIN_GRAY}99` }}>
                  {totals[i]}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Totals row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        {[
          { title: "Total Drop-offs", currentVal: sumDrop, color: Colors.PRIMARY },
          { title: "Total Pickups", currentVal: sumPick, color: Colors.SECONDARY },
        ].map(({ title, currentVal, color }) => (
          <Box
            key={title}
            sx={{
              borderRadius: "1rem",
              border: `1px solid ${Colors.ACCENT_GRAY}`,
              background: Colors.WHITE,
              p: "1rem",
            }}
          >
            <Typography
              sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: `${Colors.MAIN_GRAY}8C` }}
            >
              {title}
            </Typography>
            <Box sx={{ mt: "0.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <Box>
                <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, textTransform: "uppercase", color: `${Colors.MAIN_GRAY}80` }}>
                  Total Visits
                </Typography>
                <Typography sx={{ fontSize: "var(--fs-lg)", fontWeight: "bold", color }}>
                  {currentVal}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, textTransform: "uppercase", color: `${Colors.MAIN_GRAY}80` }}>
                  Lifetime
                </Typography>
                <Typography sx={{ fontSize: "var(--fs-lg)", fontWeight: "bold", color: Colors.MAIN_GRAY }}>
                  {totalBillItemCount ?? "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PatternsTab;
